import React, { useState, useCallback, useEffect } from 'react';
import {
    Box, Typography, Button, Chip, CircularProgress, Alert,
} from '@mui/material';

const STATUS_COLORS = {
    AKTIV: { label: 'Aktiv', bg: '#d1fae5', color: '#065f46' },
    EINGELOEST: { label: 'Eingelöst', bg: '#e5e5e3', color: '#555' },
};

const TYPE_LABELS = {
    ANFORDERUNG: 'Anforderung',
    ONLINE: 'Online',
    MANUELL: 'Manuell',
};

const fmtDate = (iso) => {
    if (!iso) return '–';
    const d = new Date(iso);
    return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })
        + ' ' + d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
};

const PinCard = ({ pin: pinRecord, sendMessage, cookies, registerMessageHandler, unregisterMessageHandler }) => {
    const [revealedPin, setRevealedPin] = useState(null);
    const [localStatus, setLocalStatus] = useState(pinRecord.status);
    const [revealing, setRevealing] = useState(false);
    const [redeeming, setRedeeming] = useState(false);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    const sc = STATUS_COLORS[localStatus] || STATUS_COLORS.AKTIV;
    const typeLabel = TYPE_LABELS[pinRecord.type] || pinRecord.type;

    const handleMessage = useCallback((msg) => {
        if (msg.pinRecordId && msg.pinRecordId !== pinRecord.pinRecordId) return;

        if (msg.task === 'respRevealPin') {
            setRevealing(false);
            if (msg.status === 'succeeded') {
                setRevealedPin(msg.pin);
                setError(null);
            } else {
                setError(msg.message || 'Fehler beim Aufdecken.');
            }
        }
        if (msg.task === 'respRedeemPin') {
            setRedeeming(false);
            if (msg.status === 'succeeded') {
                setLocalStatus('EINGELOEST');
                setRevealedPin(null);
                setSuccessMsg('PIN eingelöst.');
                setError(null);
            } else {
                setError(msg.message || 'Fehler beim Einlösen.');
            }
        }
    }, [pinRecord.pinRecordId]);

    useEffect(() => {
        if (registerMessageHandler) registerMessageHandler(handleMessage);
        return () => { if (unregisterMessageHandler) unregisterMessageHandler(); };
    }, [handleMessage, registerMessageHandler, unregisterMessageHandler]);

    const send = (task, extra = {}) => {
        setError(null);
        setSuccessMsg(null);
        sendMessage(JSON.stringify({ task, station: cookies.station, staff: cookies.staff, ...extra }));
    };

    const handleReveal = () => {
        setRevealing(true);
        send('revealPin', { pinRecordId: pinRecord.pinRecordId });
    };

    const handleRedeem = () => {
        setRedeeming(true);
        send('redeemPin', { pinRecordId: pinRecord.pinRecordId });
    };

    const isAktiv = localStatus === 'AKTIV';

    return (
        <Box sx={{
            bgcolor: '#fff', borderRadius: '10px', border: '1px solid #e5e5e3',
            px: 2.5, py: 1.5, opacity: isAktiv ? 1 : 0.7, transition: 'opacity 0.2s',
        }}>
            {/* Top row: metadata */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                {pinRecord.kdnr && (
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>
                        {pinRecord.kdnr}
                    </Typography>
                )}
                {pinRecord.kundenName && (
                    <Typography sx={{ fontSize: 13, color: '#555' }}>
                        {pinRecord.kundenName}
                    </Typography>
                )}
                {pinRecord.sendungsnummer && (
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>
                        {pinRecord.sendungsnummer}
                    </Typography>
                )}
                {pinRecord.spedition && (
                    <Chip label={pinRecord.spedition} size="small"
                        sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: '#e5e5e3' }} />
                )}
                <Chip label={sc.label} size="small"
                    sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: sc.bg, color: sc.color }} />
                <Chip label={typeLabel} size="small" variant="outlined"
                    sx={{ height: 20, fontSize: 10, fontWeight: 600, borderColor: '#ddd', color: '#777' }} />
                <Typography sx={{ fontSize: 11, color: '#999' }}>{fmtDate(pinRecord.erstelltAm)}</Typography>
            </Box>

            {/* PIN display + actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                <Typography sx={{ fontSize: 16, fontFamily: 'monospace', fontWeight: 700, color: revealedPin ? '#1a1a1a' : '#bbb' }}>
                    {revealedPin || '••••••'}
                </Typography>

                {!revealedPin && (
                    <Button
                        size="small"
                        variant="outlined"
                        disableElevation
                        onClick={handleReveal}
                        disabled={revealing || redeeming}
                        sx={{
                            borderRadius: 2, textTransform: 'none', fontSize: 12, fontWeight: 600,
                            borderColor: '#ddd', color: '#1a1a1a',
                        }}
                    >
                        {revealing ? <CircularProgress size={14} /> : 'Anzeigen'}
                    </Button>
                )}

                {isAktiv && revealedPin && (
                    <Button
                        size="small"
                        variant="contained"
                        disableElevation
                        onClick={handleRedeem}
                        disabled={redeeming}
                        sx={{
                            borderRadius: 2, textTransform: 'none', fontSize: 12, fontWeight: 600,
                        }}
                    >
                        {redeeming ? <CircularProgress size={14} /> : 'Als erledigt markieren'}
                    </Button>
                )}
            </Box>

            {/* Alerts */}
            {(error || successMsg) && (
                <Box sx={{ mt: 1 }}>
                    {error && <Alert severity="error" sx={{ borderRadius: 2, py: 0 }} onClose={() => setError(null)}>{error}</Alert>}
                    {successMsg && <Alert severity="success" sx={{ borderRadius: 2, py: 0 }} onClose={() => setSuccessMsg(null)}>{successMsg}</Alert>}
                </Box>
            )}
        </Box>
    );
};

export default PinCard;
